import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Box, CssBaseline, Drawer, IconButton, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Toolbar, Typography, Avatar, Menu, MenuItem,
  Chip, Tooltip, Badge, Divider, ListSubheader,
} from '@mui/material';
import {
  Dashboard, TrendingUp, History, Science, Assessment, Settings,
  Menu as MenuIcon, SmartToy, NotificationsNone, AccountCircle,
  FiberManualRecord, BarChart, BugReport, AccountBalanceWallet, ShoppingCart,
  CreditCard, AdminPanelSettings, EventNote, Psychology, Shield, ArrowForwardIos,
  Telegram,
} from '@mui/icons-material';
import useStore from '../../store/useStore';

import NotificationDrawer from '../NotificationCenter/NotificationDrawer';
import { useSocket } from '../../hooks/useSocket';

const DRAWER_WIDTH = 260;

const navSections = [
  {
    title: 'TRADING & MARKETS',
    items: [
      { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
      { text: 'Master Orders Terminal', icon: <ShoppingCart />, path: '/orders' },
      { text: 'Trading Terminal', icon: <TrendingUp />, path: '/trading' },
      { text: 'Testing', icon: <BugReport />, path: '/testing' },
      { text: 'AI Equity Engine', icon: <SmartToy />, path: '/equity-engine' },
      { text: 'Zerodha Portfolio', icon: <AccountBalanceWallet />, path: '/portfolio' },
    ]
  },
  {
    title: 'ANALYTICS & AUDIT',
    items: [
      { text: 'Audit Trail', icon: <EventNote />, path: '/activity-logs' },
      { text: 'Signals', icon: <FiberManualRecord />, path: '/signals' },
      { text: 'Backtest', icon: <Science />, path: '/backtest' },
      { text: 'Reports', icon: <Assessment />, path: '/reports' },
      { text: 'Notifications', icon: <NotificationsNone />, path: '/notifications' },
    ]
  },
  {
    title: 'ADMINISTRATION',
    items: [
      { text: 'Telegram Subscribers', icon: <Telegram />, path: '/admin/subscribers' },
      { text: 'Subscription Plans', icon: <CreditCard />, path: '/subscriptions' },
      { text: 'Admin Subscriptions', icon: <AdminPanelSettings />, path: '/admin/subscriptions' },
      { text: 'AI Agent Center', icon: <Psychology />, path: '/admin/agent-center' },
      { text: 'Settings', icon: <Settings />, path: '/settings' },
    ]
  }
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [subInfo, setSubInfo] = useState(null);
  const { user, logout, botStatus, notifications } = useStore();

  useSocket();

  useEffect(() => {
    import('../../services/api').then(({ default: api }) => {
      api.get('/subscriptions/current').then(res => {
        if (res.data?.subscription) {
          setSubInfo(res.data.subscription);
        }
      }).catch(() => {});
    });
  }, []);

  const isRunning = botStatus?.isRunning || botStatus?.is_running;
  const unread = (notifications || []).filter(n => !n.read && !n.is_read).length;
  const planSlug = (subInfo?.plan_slug || 'free').toLowerCase();
  const planName = subInfo?.plan_name || 'Free Trial';
  const isPremium = planSlug === 'premium';
  const isPro = planSlug === 'pro' || planName.toUpperCase() === 'PRO';
  const isAdmin = user?.role === 'admin';

  const proRestrictedPaths = [];

  useEffect(() => {
    if (!isAdmin && (location.pathname === '/admin/subscriptions' || location.pathname === '/admin/agent-center')) {
      navigate('/', { replace: true });
    }
  }, [isAdmin, location.pathname]);

  // Find active page title for header
  let activeTitle = 'Dashboard';
  navSections.forEach(section => {
    section.items.forEach(item => {
      if (item.path === location.pathname) {
        activeTitle = item.text;
      }
    });
  });

  const drawer = (
    <Box sx={{
      bgcolor: '#0f172a',
      height: '100%',
      color: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      borderRight: '1px solid #1e293b'
    }}>
      {/* Brand Header */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 40,
          height: 40,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)'
        }}>
          <SmartToy sx={{ color: '#ffffff', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2, letterSpacing: '-0.01em' }}>
            AI TRADER <Chip label="PRO" size="small" sx={{ height: 16, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#2563eb', color: '#fff', ml: 0.5 }} />
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 500 }}>
            Autonomous Trading Core
          </Typography>
        </Box>
      </Box>

      {/* Bot Status Banner */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{
          p: 1.25,
          borderRadius: 2,
          bgcolor: isRunning ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${isRunning ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: isRunning ? '#10b981' : '#ef4444',
              boxShadow: `0 0 8px ${isRunning ? '#10b981' : '#ef4444'}`
            }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: isRunning ? '#34d399' : '#f87171', letterSpacing: '0.03em' }}>
              {isRunning ? 'BOT ACTIVE' : 'BOT INACTIVE'}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 600 }}>
            PAPER
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#1e293b', mx: 2 }} />

      {/* Navigation Groups */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1.5, py: 1.5 }}>
        {navSections.map((section) => {
          const visibleItems = section.items.filter(item => {
            if (isPro && proRestrictedPaths.includes(item.path)) return false;
            if (!isAdmin && (item.path === '/admin/subscriptions' || item.path === '/admin/agent-center')) return false;
            return true;
          });

          if (visibleItems.length === 0) return null;

          return (
            <Box key={section.title} sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{
                px: 1.5,
                py: 0.75,
                display: 'block',
                color: '#64748b',
                fontWeight: 700,
                fontSize: '0.68rem',
                letterSpacing: '0.08em',
              }}>
                {section.title}
              </Typography>
              <List disablePadding>
                {visibleItems.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => { navigate(item.path); setMobileOpen(false); }}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          px: 1.5,
                          bgcolor: active ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : 'transparent',
                          backgroundColor: active ? '#2563eb' : 'transparent',
                          color: active ? '#ffffff' : '#94a3b8',
                          transition: 'all 0.15s ease-in-out',
                          '&:hover': {
                            bgcolor: active ? '#2563eb' : 'rgba(255, 255, 255, 0.05)',
                            color: '#ffffff',
                            '& .MuiListItemIcon-root': { color: '#ffffff' }
                          },
                        }}
                      >
                        <ListItemIcon sx={{
                          color: active ? '#ffffff' : '#94a3b8',
                          minWidth: 32,
                          '& .MuiSvgIcon-root': { fontSize: 20 }
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            color: active ? '#ffffff' : '#cbd5e1',
                            fontSize: '0.85rem',
                            fontWeight: active ? 700 : 500,
                            letterSpacing: '-0.01em',
                          }}
                        />
                        {active && (
                          <ArrowForwardIos sx={{ fontSize: '10px !important', color: '#ffffff', opacity: 0.8 }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* User Footer Summary */}
      <Divider sx={{ borderColor: '#1e293b' }} />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: '#2563eb', fontWeight: 700, fontSize: '0.9rem' }}>
          {user?.username ? user.username.charAt(0).toUpperCase() : 'A'}
        </Avatar>
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {user?.username || 'Administrator'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.72rem' }}>
            {user?.role === 'admin' ? 'Super Admin' : 'Trader'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <CssBaseline />

      {/* Top Header Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: '#ffffff',
          color: '#0f172a',
          borderBottom: '1px solid #e2e8f0',
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 3 }, minHeight: 64 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-0.01em', color: '#0f172a' }}>
              {activeTitle}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`PLAN: ${planName.toUpperCase()}`}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.72rem',
                bgcolor: isPremium ? '#fef3c7' : '#f1f5f9',
                color: isPremium ? '#d97706' : '#475569',
                border: `1px solid ${isPremium ? '#fde68a' : '#cbd5e1'}`,
              }}
            />
            
            <Chip
              label={isPremium ? 'Trading: ENABLED' : 'Trading: ANALYSIS ONLY'}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.72rem',
                bgcolor: isPremium ? '#ecfdf5' : '#e0f2fe',
                color: isPremium ? '#059669' : '#0369a1',
                border: `1px solid ${isPremium ? '#a7f3d0' : '#bae6fd'}`,
              }}
            />

            <Divider orientation="vertical" flexItem sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

            <Tooltip title="Notifications">
              <IconButton
                onClick={() => setNotifDrawerOpen(true)}
                sx={{
                  color: '#64748b',
                  '&:hover': { bgcolor: '#f1f5f9', color: '#0f172a' }
                }}
              >
                <Badge badgeContent={unread} color="error">
                  <NotificationsNone />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={user?.username || 'Account'}>
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ p: 0.5, border: '1px solid #e2e8f0' }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#2563eb', fontSize: '0.85rem', fontWeight: 700 }}>
                  {user?.username ? user.username.charAt(0).toUpperCase() : 'A'}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  borderRadius: 3,
                  boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.1), 0 4px 6px -4px rgba(15, 23, 42, 0.1)',
                  border: '1px solid #e2e8f0',
                  p: 1,
                }
              }}
            >
              <Box sx={{ px: 1.5, py: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{user?.username || 'User'}</Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>{user?.email}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/subscriptions'); }} sx={{ borderRadius: 1.5, fontSize: '0.875rem' }}>
                Subscription: {planName}
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/notifications'); }} sx={{ borderRadius: 1.5, fontSize: '0.875rem' }}>
                Notification Center
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/settings'); }} sx={{ borderRadius: 1.5, fontSize: '0.875rem' }}>
                Settings
              </MenuItem>
              <Divider sx={{ my: 1 }} />
              <MenuItem onClick={() => { setAnchorEl(null); logout(); navigate('/login'); }} sx={{ borderRadius: 1.5, fontSize: '0.875rem', color: '#ef4444', fontWeight: 600 }}>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer Component */}
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, border: 'none' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Drawer Overlay for Notifications */}
      <NotificationDrawer open={notifDrawerOpen} onClose={() => setNotifDrawerOpen(false)} />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2.5, sm: 3.5 },
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: 8,
          bgcolor: '#f8fafc',
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
